import { useEffect, useId, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useModalFocus } from '../hooks/useModalFocus';
import { PlanButton } from './PlanCard';
import { PlanErrorSummary } from './PlanErrorSummary';
import { formatBillingDate, formatPrice, getPlanPrice } from '../utils/planPricing';
import {
  billingErrorMessage,
  openBillingPortal,
  previewUpgrade,
  refreshBilling,
  upgradePlan,
} from '../utils/planActions';

// Confirm step for upgrading Solo -> Family (Stripe plan, phase 4). Shows exactly what will be
// charged today, from Stripe's own preview, before anything is paid. The upgrade is charged
// straight away and only applies if the payment goes through:
//   - declined card: nothing changes, an inline error with a link to update the card
//   - bank wants confirmation: a link to Stripe's secure page, then "Check again"
// Errors are inline (no toasts). Keyboard behaviour via useModalFocus.

const ADDED_TO_NEXT_BILL_BELOW = 50; // cents: Stripe can't charge less than about 50c

export function UpgradeConfirmDrawer({ isOpen, interval, onClose, onUpgraded }) {
  const panelRef = useRef(null);
  const titleId = useId();
  // loading | ready | paying | confirm-with-bank | checking
  const [phase, setPhase] = useState('loading');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [declined, setDeclined] = useState(false);
  const [bankUrl, setBankUrl] = useState('');

  useModalFocus({
    isOpen,
    panelRef,
    onClose,
    stepKey: phase,
    canClose: phase !== 'paying' && phase !== 'checking',
  });

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setPhase('loading');
    setPreview(null);
    setError('');
    setDeclined(false);
    setBankUrl('');
    (async () => {
      try {
        const result = await previewUpgrade({ interval });
        if (cancelled) return;
        setPreview(result);
        setPhase('ready');
      } catch (err) {
        if (cancelled) return;
        console.warn('Upgrade preview failed:', err);
        setError(billingErrorMessage(err, "We couldn't work out the cost of upgrading. Please try again."));
        setPhase('ready');
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, interval]);

  if (!isOpen) return null;

  const amountDue = Number(preview?.amountDue) || 0;
  const dueText = formatPrice(amountDue / 100);
  const renewalText = formatBillingDate(preview?.renewalDate);
  const priceText = formatPrice(getPlanPrice('family', interval));
  const per = interval === 'yearly' ? 'year' : 'month';
  const busy = phase === 'paying' || phase === 'checking';

  const pay = async () => {
    setError('');
    setDeclined(false);
    setPhase('paying');
    try {
      const result = await upgradePlan({ interval, prorationDate: preview?.prorationDate });
      if (result?.status === 'requires_action' && result.url) {
        setBankUrl(result.url);
        setPhase('confirm-with-bank');
        return;
      }
      onUpgraded(result?.profile || null);
    } catch (err) {
      console.warn('Upgrade failed:', err);
      setDeclined(err?.status === 402);
      setError(billingErrorMessage(err, 'Something went wrong with your upgrade. Please refresh Plan & billing to check your plan before trying again.'));
      setPhase('ready');
    }
  };

  // After confirming on Stripe's page in another tab.
  const checkAgain = async () => {
    setError('');
    setPhase('checking');
    try {
      const { profile } = await refreshBilling();
      if (profile?.subscriptionPlan === 'family') {
        onUpgraded(profile);
        return;
      }
      setError("Your payment hasn't been confirmed yet, so you're still on Solo. Finish confirming on Stripe's page, then check again.");
    } catch (err) {
      console.warn('Upgrade check failed:', err);
      setError(billingErrorMessage(err, "We couldn't check your plan just now. Please try again."));
    }
    setPhase('confirm-with-bank');
  };

  const linkClass = 'mpg-focus-ring inline-flex min-h-[44px] items-center rounded-md text-base font-semibold text-[color:var(--plan-solo-text)] underline underline-offset-4 hover:text-[color:var(--plan-heading-text)]';

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={busy ? undefined : onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="fixed bottom-0 left-0 right-0 z-50 mx-auto max-h-[90vh] max-w-[600px] overflow-y-auto rounded-t-3xl bg-white animate-slide-up focus:outline-none"
      >
        <div className="flex justify-center pb-2 pt-3" aria-hidden="true">
          <div className="h-1 w-10 rounded-full bg-gray-300" />
        </div>
        <div className="px-6 pb-8 pt-2 text-[color:var(--plan-heading-text)]">
          <div className="mb-4 flex items-start justify-between gap-4">
            <h2 id={titleId} className="text-2xl font-bold">
              Upgrade to Family{interval === 'yearly' ? ' yearly' : ''}
            </h2>
            {!busy && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="mpg-focus-ring -mr-2 -mt-2 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full hover:bg-gray-50"
              >
                <X className="h-5 w-5 text-gray-600" aria-hidden="true" />
              </button>
            )}
          </div>

          <PlanErrorSummary message={error} title="Something needs your attention" />
          {declined && (
            <p className="-mt-2 mb-4">
              <button type="button" className={linkClass} onClick={() => openBillingPortal({ flow: 'manage' }).catch(() => {})}>
                Update your card
              </button>
            </p>
          )}

          {phase === 'loading' && (
            <p role="status" className="text-base text-[color:var(--plan-muted-text)]">Working out the cost…</p>
          )}

          {preview && (phase === 'ready' || phase === 'paying') && (
            <>
              <div className="rounded-2xl border-2 border-[color:var(--plan-card-border)] p-5">
                <p className="text-base">{amountDue > 0 ? 'Due today' : 'Nothing to pay today'}</p>
                {amountDue > 0 && <p className="mt-1 text-4xl font-bold">{dueText}</p>}
                <p className="mt-3 text-base leading-7">
                  {preview.intervalChange
                    ? `That's a year of Family, minus what's left of your Solo plan. Your plan then renews every year${renewalText ? ` from ${renewalText}` : ''}.`
                    : `That's the difference between Solo and Family for the rest of your current billing period${renewalText ? `, to ${renewalText}` : ''}.`}
                </p>
                <p className="mt-2 text-base leading-7">
                  Then {priceText} per {per}{renewalText ? ` from ${renewalText}` : ''}, until you cancel.
                </p>
                {amountDue > 0 && amountDue < ADDED_TO_NEXT_BILL_BELOW && (
                  <p className="mt-2 text-sm text-[color:var(--plan-muted-text)]">
                    Amounts under 50c can&apos;t be charged on their own, so this is added to your next bill instead.
                  </p>
                )}
              </div>
              <p className="mt-4 text-base leading-7">
                Family starts as soon as the payment goes through, and you can invite up to 3 household members.
              </p>
              <p className="mt-2 text-sm text-[color:var(--plan-muted-text)]">
                Your saved card is charged. Secure payment with Stripe.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row-reverse">
                <PlanButton busy={phase === 'paying'} onClick={pay}>
                  {phase === 'paying' ? 'Upgrading…' : amountDue > 0 ? `Pay ${dueText} and upgrade` : 'Upgrade now'}
                </PlanButton>
                <PlanButton variant="secondary" onClick={onClose} disabled={phase === 'paying'}>
                  Cancel
                </PlanButton>
              </div>
            </>
          )}

          {(phase === 'confirm-with-bank' || phase === 'checking') && (
            <>
              <div role="status" className="rounded-2xl bg-[var(--plan-notice-bg)] p-5 text-[color:var(--plan-notice-text)]">
                <p className="font-bold">Your bank needs you to confirm this payment</p>
                <p className="mt-2 leading-7">
                  You&apos;re still on Solo and nothing has been taken yet. Confirm the payment on Stripe&apos;s secure page,
                  which opens in a new tab, then come back here and check again.
                </p>
              </div>
              <div className="mt-6 flex flex-col gap-3">
                <a
                  href={bankUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mpg-focus-ring inline-flex min-h-[44px] items-center justify-center rounded-full bg-[var(--plan-button)] px-6 py-3 text-base font-semibold text-[color:var(--plan-button-text)] hover:bg-[var(--plan-button-hover)]"
                >
                  Confirm payment (opens a new tab)
                </a>
                <PlanButton variant="secondary" busy={phase === 'checking'} onClick={checkAgain}>
                  {phase === 'checking' ? 'Checking…' : "I've confirmed it, check again"}
                </PlanButton>
              </div>
              <p className="mt-4 text-sm text-[color:var(--plan-muted-text)]">
                If you don&apos;t confirm it, the upgrade is dropped within a day and you stay on Solo.
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default UpgradeConfirmDrawer;
